import React, { useState, useEffect } from "react";
import { FiX, FiUpload, FiTrash2, FiPlus, FiSave } from "react-icons/fi";
import { uploadFiles } from "../../API/FileAPI/FileAPI";
import toast from "react-hot-toast";

const EditMerchandiseDetailsModal = ({
  isOpen,
  onClose,
  onSubmit,
  requestData,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    design: [],
    productWithDesign: "",
    price: 0,
    color: [],
    size: [],
    productLink: "",
  });
  const [newColor, setNewColor] = useState("#000000");
  const [uploadingDesign, setUploadingDesign] = useState(false);
  const [uploadingProductDesign, setUploadingProductDesign] = useState(false);
  const [designPreview, setDesignPreview] = useState(null);
  const [productDesignPreview, setProductDesignPreview] = useState(null);
  const [isDraggingDesign, setIsDraggingDesign] = useState(false);
  const [isDraggingProduct, setIsDraggingProduct] = useState(false);

  // Available sizes
  const availableSizes = ["S", "M", "L", "XL", "XXL"];

  // Initialize form with existing data
  useEffect(() => {
    if (requestData?.merchandiseProductRequest) {
      const request = requestData.merchandiseProductRequest;
      setFormData({
        design: request.design || [],
        productWithDesign: request.productWithDesign || "",
        price: request.price || 0,
        color: request.color || [],
        size: request.size || [],
        productLink: request.productLink || "",
      });
    }
  }, [requestData]);

  const handleImageUpload = async (file, type) => {
    try {
      // Show preview immediately
      const previewUrl = URL.createObjectURL(file);
      if (type === "design") {
        setDesignPreview(previewUrl);
        setUploadingDesign(true);
      } else {
        setProductDesignPreview(previewUrl);
        setUploadingProductDesign(true);
      }

      // Upload in background
      const response = await uploadFiles(file);

      if (response && response.photos && response.photos.length > 0) {
        if (type === "design") {
          setFormData({ ...formData, design: [response.photos[0]] });
        } else {
          setFormData({ ...formData, productWithDesign: response.photos[0] });
        }
        toast.success("Image uploaded successfully");
      } else {
        toast.error("Failed to upload image");
        // Remove preview on failure
        if (type === "design") {
          setDesignPreview(null);
        } else {
          setProductDesignPreview(null);
        }
      }
    } catch (error) {
      toast.error("Error uploading image");
      console.error("Upload error:", error);
      // Remove preview on error
      if (type === "design") {
        setDesignPreview(null);
      } else {
        setProductDesignPreview(null);
      }
    } finally {
      if (type === "design") {
        setUploadingDesign(false);
      } else {
        setUploadingProductDesign(false);
      }
    }
  };

  // Drag and drop handlers
  const handleDragOver = (e, type) => {
    e.preventDefault();
    if (type === "design") {
      setIsDraggingDesign(true);
    } else {
      setIsDraggingProduct(true);
    }
  };

  const handleDragLeave = (e, type) => {
    e.preventDefault();
    if (type === "design") {
      setIsDraggingDesign(false);
    } else {
      setIsDraggingProduct(false);
    }
  };

  const handleDrop = (e, type) => {
    e.preventDefault();
    if (type === "design") {
      setIsDraggingDesign(false);
    } else {
      setIsDraggingProduct(false);
    }

    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith("image/")) {
      handleImageUpload(file, type);
    } else {
      toast.error("Please drop an image file");
    }
  };

  const handleAddColor = () => {
    if (newColor && !formData.color.includes(newColor)) {
      setFormData({ ...formData, color: [...formData.color, newColor] });
      setNewColor("#000000");
    } else if (formData.color.includes(newColor)) {
      toast.error("Color already added");
    }
  };

  const handleRemoveColor = (colorToRemove) => {
    setFormData({
      ...formData,
      color: formData.color.filter((c) => c !== colorToRemove),
    });
  };

  const handleToggleSize = (size) => {
    if (formData.size.includes(size)) {
      setFormData({
        ...formData,
        size: formData.size.filter((s) => s !== size),
      });
    } else {
      setFormData({ ...formData, size: [...formData.size, size] });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Prepare payload - only send fields that were changed
      const payload = {};

      // Check if design changed
      if (
        formData.design[0] !== requestData.merchandiseProductRequest.design[0]
      ) {
        payload.design = formData.design;
      }

      // Check if productWithDesign changed
      if (
        formData.productWithDesign !==
        requestData.merchandiseProductRequest.productWithDesign
      ) {
        payload.productWithDesign = formData.productWithDesign;
      }

      // Check if price changed
      if (formData.price !== requestData.merchandiseProductRequest.price) {
        payload.price = formData.price;
      }

      // Check if color changed
      if (
        JSON.stringify(formData.color) !==
        JSON.stringify(requestData.merchandiseProductRequest.color)
      ) {
        payload.color = formData.color;
      }

      // Check if size changed
      if (
        JSON.stringify(formData.size) !==
        JSON.stringify(requestData.merchandiseProductRequest.size)
      ) {
        payload.size = formData.size;
      }

      // Check if productLink changed
      if (
        formData.productLink !==
        (requestData.merchandiseProductRequest.productLink || "")
      ) {
        payload.productLink = formData.productLink;
      }

      // If nothing changed, show message
      if (Object.keys(payload).length === 0) {
        toast.info("No changes detected");
        setIsSubmitting(false);
        return;
      }

      await onSubmit(requestData.merchandiseProductRequest._id, payload);
    } catch (error) {
      console.error("Error updating request:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Edit Request Details
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              {requestData?.merchandiseProduct?.title || "Product"}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-6">
            {/* Design Image Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Design Image
              </label>
              <div className="flex items-start gap-4">
                {/* Preview Box */}
                <div
                  className={`relative w-32 h-32 rounded-lg border-2 transition-all ${
                    isDraggingDesign
                      ? "border-[--primary] border-solid bg-blue-50"
                      : "border-dashed border-gray-300"
                  }`}
                  onDragOver={(e) => handleDragOver(e, "design")}
                  onDragLeave={(e) => handleDragLeave(e, "design")}
                  onDrop={(e) => handleDrop(e, "design")}
                >
                  {designPreview || formData.design[0] ? (
                    <>
                      <img
                        src={designPreview || formData.design[0]}
                        alt="Design"
                        className="w-full h-full object-cover rounded-lg"
                      />
                      {uploadingDesign && (
                        <div className="absolute inset-0 bg-black bg-opacity-50 rounded-lg flex items-center justify-center">
                          <div className="text-center text-white">
                            <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto mb-1"></div>
                            <span className="text-xs">Uploading...</span>
                          </div>
                        </div>
                      )}
                      {!uploadingDesign && (
                        <button
                          type="button"
                          onClick={() => {
                            setFormData({ ...formData, design: [] });
                            setDesignPreview(null);
                          }}
                          className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors shadow-md"
                        >
                          <FiTrash2 className="w-4 h-4" />
                        </button>
                      )}
                    </>
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center text-gray-400">
                      <FiUpload className="w-8 h-8 mb-1" />
                      <span className="text-xs">Drop here</span>
                    </div>
                  )}
                </div>

                {/* Upload Button */}
                <div className="flex-1">
                  <label
                    htmlFor="design-upload"
                    className={`inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer ${
                      uploadingDesign ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                  >
                    {uploadingDesign ? (
                      <>
                        <div className="w-4 h-4 border-2 border-gray-600 border-t-transparent rounded-full animate-spin"></div>
                        Uploading...
                      </>
                    ) : (
                      <>
                        <FiUpload className="w-4 h-4" />
                        Choose File
                      </>
                    )}
                  </label>
                  <input
                    id="design-upload"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      if (e.target.files[0]) {
                        handleImageUpload(e.target.files[0], "design");
                      }
                    }}
                    disabled={uploadingDesign}
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    📤 Drag & drop or click to upload
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Design will be applied to the product
                  </p>
                </div>
              </div>
            </div>

            {/* Product With Design Image Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Final Product Preview (Optional)
              </label>
              <div className="flex items-start gap-4">
                {/* Preview Box */}
                <div
                  className={`relative w-32 h-32 rounded-lg border-2 transition-all ${
                    isDraggingProduct
                      ? "border-[--primary] border-solid bg-blue-50"
                      : "border-dashed border-gray-300"
                  }`}
                  onDragOver={(e) => handleDragOver(e, "productWithDesign")}
                  onDragLeave={(e) => handleDragLeave(e, "productWithDesign")}
                  onDrop={(e) => handleDrop(e, "productWithDesign")}
                >
                  {productDesignPreview || formData.productWithDesign ? (
                    <>
                      <img
                        src={productDesignPreview || formData.productWithDesign}
                        alt="Product with Design"
                        className="w-full h-full object-cover rounded-lg"
                      />
                      {uploadingProductDesign && (
                        <div className="absolute inset-0 bg-black bg-opacity-50 rounded-lg flex items-center justify-center">
                          <div className="text-center text-white">
                            <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto mb-1"></div>
                            <span className="text-xs">Uploading...</span>
                          </div>
                        </div>
                      )}
                      {!uploadingProductDesign && (
                        <button
                          type="button"
                          onClick={() => {
                            setFormData({ ...formData, productWithDesign: "" });
                            setProductDesignPreview(null);
                          }}
                          className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors shadow-md"
                        >
                          <FiTrash2 className="w-4 h-4" />
                        </button>
                      )}
                    </>
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center text-gray-400">
                      <FiUpload className="w-8 h-8 mb-1" />
                      <span className="text-xs">Drop here</span>
                    </div>
                  )}
                </div>

                {/* Upload Button */}
                <div className="flex-1">
                  <label
                    htmlFor="product-design-upload"
                    className={`inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer ${
                      uploadingProductDesign
                        ? "opacity-50 cursor-not-allowed"
                        : ""
                    }`}
                  >
                    {uploadingProductDesign ? (
                      <>
                        <div className="w-4 h-4 border-2 border-gray-600 border-t-transparent rounded-full animate-spin"></div>
                        Uploading...
                      </>
                    ) : (
                      <>
                        <FiUpload className="w-4 h-4" />
                        Choose File
                      </>
                    )}
                  </label>
                  <input
                    id="product-design-upload"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      if (e.target.files[0]) {
                        handleImageUpload(
                          e.target.files[0],
                          "productWithDesign"
                        );
                      }
                    }}
                    disabled={uploadingProductDesign}
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    📤 Drag & drop or click to upload
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Preview of final product with design
                  </p>
                </div>
              </div>
            </div>

            {/* Price */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Price ($)
              </label>
              <input
                type="number"
                value={formData.price}
                onChange={(e) =>
                  setFormData({ ...formData, price: Number(e.target.value) })
                }
                min="0"
                step="0.01"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--primary] focus:border-transparent transition-colors"
                placeholder="Enter price"
              />
            </div>

            {/* Product Link */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Product Link (Optional)
              </label>
              <input
                type="url"
                value={formData.productLink}
                onChange={(e) =>
                  setFormData({ ...formData, productLink: e.target.value })
                }
                placeholder="https://example.com/product"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--primary] focus:border-transparent transition-colors"
              />
              <p className="text-xs text-gray-500 mt-2">
                🔗 Link to the product page where customers can purchase
              </p>
            </div>

            {/* Colors */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Colors
              </label>
              {/* Color List */}
              {formData.color.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3">
                  {formData.color.map((color, idx) => (
                    <div
                      key={idx}
                      className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-lg"
                    >
                      <div
                        className="w-6 h-6 rounded border border-gray-300"
                        style={{ backgroundColor: color }}
                      ></div>
                      <span className="text-sm text-gray-700">{color}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveColor(color)}
                        className="text-red-500 hover:text-red-700 transition-colors"
                      >
                        <FiTrash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              {/* Add Color */}
              <div className="flex gap-2">
                <input
                  type="color"
                  value={newColor}
                  onChange={(e) => setNewColor(e.target.value)}
                  className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                />
                <input
                  type="text"
                  value={newColor}
                  onChange={(e) => setNewColor(e.target.value)}
                  placeholder="#000000"
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--primary] focus:border-transparent transition-colors"
                />
                <button
                  type="button"
                  onClick={handleAddColor}
                  className="px-4 py-2 bg-[--primary] text-white rounded-lg hover:bg-[--primary]/90 transition-colors flex items-center gap-2"
                >
                  <FiPlus className="w-4 h-4" />
                  Add
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Add hex color codes (e.g., #FF5733)
              </p>
            </div>

            {/* Sizes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Available Sizes
              </label>
              <div className="flex flex-wrap gap-2">
                {availableSizes.map((size) => (
                  <button
                    key={size}
                    type="button"
                    onClick={() => handleToggleSize(size)}
                    className={`px-4 py-2 rounded-lg border-2 transition-colors font-medium ${
                      formData.size.includes(size)
                        ? "border-[--primary] bg-[--primary] text-white"
                        : "border-gray-300 bg-white text-gray-700 hover:border-gray-400"
                    }`}
                  >
                    {size}
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Select all available sizes for this product
              </p>
            </div>
          </div>

          {/* Footer Actions */}
          <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={
                isSubmitting || uploadingDesign || uploadingProductDesign
              }
              className="bg-[--primary] text-white px-6 py-3 rounded-lg hover:bg-[--primary]/90 transition-colors font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Updating...
                </>
              ) : (
                <>
                  <FiSave className="w-4 h-4" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditMerchandiseDetailsModal;
